import {
  Gender,
  ID,
  ListApiResponse,
  SingleApiResponse
} from '@ianesthesie-kit/services';
import {
  appliesToFemales,
  appliesToMales,
  isTranslatable,
  MedicalHo,
  MedicalHoItem,
  MedicalHoItemCategory,
  needsQuestionTranslation
} from './medicalHo.model';
import { MedicalResponse, SingleMedicalService } from '../baseMedical.service';
import axios, { AxiosResponse } from 'axios';
import {
  isUserInput,
  max,
  MedicalScoreName,
  sortEmphasis,
  SourceType,
  SurgicalEvent
} from '@/modules/surgicalEvent';
import { captureThrowOnDev } from '@/util';
import { SnomedService } from '@/modules/snomed/snomed.service';
import { SentryService } from '@ianesthesie-kit/sentry';
import noop from 'lodash/fp/noop';
import { parseDateOrNull } from '@ianesthesie-kit/core';
import { AppConfigService } from '@/config';

type QcodeMap = Partial<Record<MedicalScoreName, string>>;
type QcodeMapInverted = Record<string, MedicalScoreName>;

type MedicalHoMutationArg = {
  category: MedicalHoItemCategory;
  qcode: string;
  code?: string;
};

export class MedicalHoService extends SingleMedicalService<MedicalHo> {
  public static readonly SC_CLEARED_CODE = 'SC_CLEARED_CODE';

  private static INSTANCE_BY_ID: MedicalHoService;
  private static scoreQcodeMap?: QcodeMap;
  private static scoreQcodeMapInverted?: QcodeMapInverted;
  private static scoreQcodes?: string[];
  private static notInteractiveScoreQcodes?: string[];
  private static approximateDateQcodes?: string[];

  private static readyPromise: Promise<void>;

  private scClearedCategories: Partial<Record<MedicalHoItemCategory, boolean>> =
    {};

  private removedItemsWhileMerging: MedicalHoMutationArg[] = [];

  private constructor(patientId: string) {
    super('/medical-ho', patientId);
    if (!MedicalHoService.readyPromise) {
      MedicalHoService.readyPromise = MedicalHoService.init();
    }
  }

  public static get ready(): Promise<void> {
    if (!this.readyPromise) {
      this.readyPromise = MedicalHoService.init();
    }
    return this.readyPromise;
  }

  public static getInstance(patientId: ID): MedicalHoService {
    if (this.shouldCreateNewInstance(this.INSTANCE_BY_ID, patientId)) {
      this.INSTANCE_BY_ID = new MedicalHoService(patientId);
    }
    return this.INSTANCE_BY_ID;
  }

  public static isDisplayableForSurgicalEvent(
    category: MedicalHoItemCategory,
    se: SurgicalEvent | undefined
  ) {
    if (category === MedicalHoItemCategory.BILLING) {
      return false;
    }
    if (!se?.patient) {
      return true;
    }
    if (se.patient.gender === Gender.MALE) {
      return appliesToMales(category);
    }
    if (se.patient.gender === Gender.FEMALE) {
      return appliesToFemales(category);
    }

    return true;
  }

  public static getScoreNameByQcode(qcode: string | undefined) {
    if (!qcode) {
      return undefined;
    }
    return this.scoreQcodeMapInverted?.[qcode];
  }

  public static getQcodeByScoreName(score: MedicalScoreName | undefined) {
    if (!score) {
      return undefined;
    }
    return this.scoreQcodeMap?.[score];
  }

  private static async init() {
    const promises: Promise<any>[] = [];
    if (!MedicalHoService.scoreQcodes) {
      promises.push(MedicalHoService.populateScoreQcodes());
    }
    if (!MedicalHoService.approximateDateQcodes) {
      promises.push(MedicalHoService.populateApproximateDateQcodes());
    }

    return Promise.all(promises).then(noop);
  }

  private static async populateApproximateDateQcodes() {
    await axios
      .get<ListApiResponse<string>>(
        '/bff/doctor/medical-ho/approximate-date-qcode'
      )
      .then((resp) => {
        this.approximateDateQcodes = resp.data.data;
      })
      .catch(captureThrowOnDev);
  }

  private static async populateScoreQcodes() {
    await this.populateAllScoreQcodes();
    await this.populateNotInteractiveScoreQcodes();
  }

  private static async populateAllScoreQcodes() {
    await axios
      .get<SingleApiResponse<QcodeMap>>('/bff/doctor/medical-ho/score-qcode')
      .then((resp) => {
        this.scoreQcodeMap = resp.data.data;
        if (this.scoreQcodeMap) {
          this.scoreQcodeMapInverted = Object.entries(
            this.scoreQcodeMap
          ).reduce((acc, [name, qcode]) => ({ ...acc, [qcode]: name }), {});
          this.scoreQcodes = Object.values(this.scoreQcodeMap);
        }
      })
      .catch(captureThrowOnDev);
  }

  private static async populateNotInteractiveScoreQcodes() {
    await axios
      .get<SingleApiResponse<QcodeMap>>(
        '/bff/doctor/medical-ho/score-qcode?editable=false'
      )
      .then((resp) => {
        this.notInteractiveScoreQcodes = Object.values(resp.data.data);
      })
      .catch(captureThrowOnDev);
  }

  fetch(
    params: Record<string, any> = {}
  ): Promise<MedicalResponse<MedicalHo, false>> {
    return super
      .fetch(params)
      .then((resp) => {
        const newScClearedCategories: typeof this.scClearedCategories = {};
        const filteredMho = Object.entries(resp.data).reduce(
          (acc, [key, val]) => {
            let newVal: MedicalHo[MedicalHoItemCategory] = val as any;
            if (Array.isArray(newVal)) {
              newVal
                .filter((mho) => mho.code === MedicalHoService.SC_CLEARED_CODE)
                .forEach((cleared) => {
                  newScClearedCategories[cleared.category] = true;
                });
              newVal = newVal.filter(
                (mho) => mho.code !== MedicalHoService.SC_CLEARED_CODE
              );
            }

            return {
              ...acc,
              [key]: newVal
            };
          },
          resp.data
        );
        this.scClearedCategories = newScClearedCategories;
        return {
          ...resp,
          data: filteredMho
        };
      })
      .then(async (resp) => {
        await this.updateSnomedCache(resp.data);
        return resp;
      });
  }

  isCleared(category: MedicalHoItemCategory): boolean {
    return !!category && !!this.scClearedCategories[category];
  }

  isNotCleared(category: MedicalHoItemCategory): boolean {
    return !!category && !this.isCleared(category);
  }

  getRemovedItemsWhenMerging() {
    return this.removedItemsWhileMerging;
  }

  upsert(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    item: Partial<MedicalHo>,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    params?: Record<string, any>
  ): Promise<MedicalResponse<MedicalHo, false>> {
    throw new Error('Unsupported operation');
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  delete(item: ID | MedicalHo): Promise<MedicalResponse<MedicalHo, false>> {
    throw new Error('Unsupported operation');
  }

  public addItems(
    items: Partial<MedicalHoItem>[]
  ): Promise<MedicalResponse<MedicalHo, false>> {
    const payload = items.map((it) => this.transformToRequestBody(it));
    return axios
      .post(`${this.url}?sc-list`, payload)
      .then(
        (response: AxiosResponse<SingleApiResponse<MedicalHo>>) => response.data
      )
      .then((responseData) => ({
        data: this.transformResponseEntity(responseData.data)
      }))
      .catch((err) => {
        throw this.unwrapErrorDetails(err) || err;
      });
  }

  public addItem(
    item: Partial<MedicalHoItem>
  ): Promise<MedicalResponse<MedicalHo, false>> {
    return axios
      .post(this.url, this.transformToRequestBody(item))
      .then(
        (response: AxiosResponse<SingleApiResponse<MedicalHo>>) => response.data
      )
      .then((responseData) => ({
        data: this.transformResponseEntity(responseData.data)
      }))
      .catch((err) => {
        throw this.unwrapErrorDetails(err) || err;
      });
  }

  public deleteItem(
    item: MedicalHoItem
  ): Promise<MedicalResponse<MedicalHo, false>> {
    return this.deletePromise(item)
      .then(
        (response: AxiosResponse<SingleApiResponse<MedicalHo>>) => response.data
      )
      .then((responseData) => ({
        data: this.transformResponseEntity(responseData.data)
      }))
      .catch((err) => {
        throw this.unwrapErrorDetails(err) || err;
      });
  }

  protected transformResponseEntity(data: any): MedicalHo {
    const now = new Date();
    const medicalHo = Object.entries(data).reduce(
      (acc, [key, list]) => ({
        ...acc,
        [key]: ((list as MedicalHoItem[]) || [])
          .map((it) => this.setScoreField(it))
          .map((it) => this.setApproximateDateField(it, now))
          .map((it) =>
            this.correctUserInputHeartAttackHODetailsTranslationQCode(it)
          )
          .sort((it1, it2) => this.compare(it1, it2))
      }),
      {} as MedicalHo
    );

    return this.mergeWhereApplicable(medicalHo);
  }

  protected transformToRequestBody(
    _data: Partial<MedicalHoItem>
  ): Record<string, unknown> {
    const data: any = _data;
    if (data.name && data.name === data.code) {
      data.code = undefined;
    } else if (data.code) {
      data.name = undefined;
    }
    return super.transformToRequestBody(data);
  }

  private compare(it1: MedicalHoItem, it2: MedicalHoItem): number {
    const { qcode: q1, emphasis: em1 } = it1;
    const { qcode: q2, emphasis: em2 } = it2;

    const emphResult = sortEmphasis(em1, em2);

    if (emphResult) {
      return emphResult;
    }

    if (!q1 && !q2) {
      return 0;
    }
    if (q1 === q2) {
      return 0;
    }
    if (!q1) {
      return 1;
    }
    if (!q2) {
      return -1;
    }
    return q1.localeCompare(q2);
  }

  private deletePromise(item: MedicalHoItem) {
    return item.mergedWith?.length
      ? axios.delete(`${this.url}`, {
          data: {
            ids: item.mergedWith
              .map((it) => it.id)
              .concat(item.id)
              .concat(item.removedWhenMergedIds ?? [])
          }
        })
      : axios.delete(`${this.url}/${item.id}`);
  }

  private setScoreField(it: MedicalHoItem) {
    if (it?.qcode) {
      it.isScore = MedicalHoService.notInteractiveScoreQcodes?.includes(
        it.qcode
      )
        ? 'notInteractive'
        : MedicalHoService.scoreQcodes?.includes(it.qcode);
    }

    return it;
  }

  private setApproximateDateField(it: MedicalHoItem, now: Date) {
    if (
      it?.qcode &&
      MedicalHoService.approximateDateQcodes?.includes(it.qcode)
    ) {
      const value = parseDateOrNull(it.code);
      if (value && now.getFullYear() - value.getFullYear() > 2) {
        it.isApproximateDate = 'yearOnly';
      } else {
        it.isApproximateDate = true;
      }
    }

    return it;
  }

  private async updateSnomedCache(medicalHo: MedicalHo) {
    const mhoItemsWithSnomed = Object.values(medicalHo)
      .filter(Array.isArray)
      .flat()
      .filter((it) => !!it.snomedCode);

    if (mhoItemsWithSnomed.length) {
      return SnomedService.getInstance().cacheSnomedItems(mhoItemsWithSnomed);
    }
  }

  private mergeWhereApplicable(medicalHo: MedicalHo) {
    this.removedItemsWhileMerging = [];
    if (!medicalHo) {
      return medicalHo;
    }

    const transformed = { ...medicalHo };

    //Don't display "TwoFlightsStairs.YES" and "WalkTwoBlocks.YES" badges if "RegularSport.YES" exists
    if (
      this.hasMho(
        {
          category: MedicalHoItemCategory.CARDIO_VASCULAR,
          qcode: 'RegularSport',
          code: 'YES'
        },
        transformed
      )
    ) {
      this.removeMhoInPlace(
        {
          category: MedicalHoItemCategory.CARDIO_VASCULAR,
          qcode: 'TwoFlightsStairs',
          code: 'YES'
        },
        transformed
      );
      this.removeMhoInPlace(
        {
          category: MedicalHoItemCategory.CARDIO_VASCULAR,
          qcode: 'WalkTwoBlocks',
          code: 'YES'
        },
        transformed
      );
    }

    //Don't display "WalkTwoBlocks.YES" badge if "TwoFlightsStairs.YES" exists
    if (
      this.hasMho(
        {
          category: MedicalHoItemCategory.CARDIO_VASCULAR,
          qcode: 'TwoFlightsStairs',
          code: 'YES'
        },
        transformed
      )
    ) {
      this.removeMhoInPlace(
        {
          category: MedicalHoItemCategory.CARDIO_VASCULAR,
          qcode: 'WalkTwoBlocks',
          code: 'YES'
        },
        transformed
      );
    }

    this.mergeEpisodeMhoWithRespectiveDatesInPlace(transformed);
    this.mergeSmokingMhoInPlace(transformed);
    this.mergeAsthmaInPlace(transformed);
    this.mergeHeartAttackHoInPlace(transformed);
    this.mergeHOVenousThrombosisInPlace(transformed);
    this.mergeStrokeHoInPlace(transformed);
    this.mergeRaiScoreInPlace(transformed);
    this.mergeLegalGuardianInPlace(transformed);
    this.mergePrematureBirthInPlace(transformed);

    return transformed;
  }

  private hasMho(arg: MedicalHoMutationArg, medicalHo: MedicalHo) {
    return !!this.findMho(arg, medicalHo)?.length;
  }

  private findMho(
    { category, qcode, code }: MedicalHoMutationArg,
    medicalHo: MedicalHo
  ) {
    const mhos = medicalHo[category]?.filter(
      (it) => it.qcode === qcode && (!code || it.code === code)
    );

    if (!mhos?.length) {
      return undefined;
    }

    return mhos;
  }

  private findSingleMho(arg: MedicalHoMutationArg, medicalHo: MedicalHo) {
    const mhos = this.findMho(arg, medicalHo);
    if (mhos && mhos.length > 1) {
      const err = new Error(`Multiple mho found: ${arg.qcode} ${arg.code}`);
      SentryService.getInstance().captureException(err);
      if (AppConfigService.getInstance().env === 'dev') {
        console.error(err);
      }
    }
    return mhos?.[0];
  }

  private removeMhoInPlace(arg: MedicalHoMutationArg, medicalHo: MedicalHo) {
    const { category, qcode, code } = arg;
    const initialLength = medicalHo[category]?.length;
    medicalHo[category] = medicalHo[category]?.filter(
      (it) => it.qcode !== qcode || (!!code && it.code !== code)
    );

    if (initialLength !== medicalHo[category]?.length) {
      this.removedItemsWhileMerging.push(arg);
    }
  }

  private mergeMhoInPlace(
    arg: { mergeFrom: MedicalHoMutationArg; mergeTo: MedicalHoMutationArg },
    medicalHo: MedicalHo,
    mapBeforeMerge?: (mho: MedicalHoItem, which: 'from' | 'to') => MedicalHoItem
  ) {
    const mergeFrom = this.findMho(arg.mergeFrom, medicalHo);
    const mergeTo = mergeFrom && this.findSingleMho(arg.mergeTo, medicalHo);

    if (!mergeFrom || !mergeTo) {
      return;
    }

    this.appendMergedItemsInPlace(mergeTo, mergeFrom, mapBeforeMerge);
    this.removeMhoInPlace(arg.mergeFrom, medicalHo);
  }

  private appendMergedItemsInPlace(
    mergeTo: MedicalHoItem,
    mergeFrom: MedicalHoItem[],
    mapBeforeMerge?: (mho: MedicalHoItem, which: 'from' | 'to') => MedicalHoItem
  ) {
    mergeFrom = mergeFrom.map((mho) => mapBeforeMerge?.(mho, 'from') ?? mho);
    mergeTo = mapBeforeMerge?.(mergeTo, 'to') ?? mergeTo;

    // keep user inputs at the end
    const alreadyMergedUserInputs = mergeTo.mergedWith?.filter(
      ({ sourceType }) => isUserInput(sourceType)
    );
    mergeTo.mergedWith = (mergeTo.mergedWith ?? [])
      .filter(({ sourceType }) => !isUserInput(sourceType))
      .concat(mergeFrom)
      .concat(alreadyMergedUserInputs ?? []);
    for (const mho of mergeFrom) {
      mergeTo.emphasis = max(mergeTo.emphasis, mho.emphasis);
      if (mho.notes) {
        if (mergeTo.notes) {
          mergeTo.notes += '\n';
        } else {
          mergeTo.notes = '';
        }
        mergeTo.notes += mho.notes;
      }
    }
  }

  private mergeEpisodeMhoWithRespectiveDatesInPlace(medicalHo: MedicalHo) {
    if (!medicalHo) {
      return;
    }

    const doMerge = (
      category: MedicalHoItemCategory,
      from: Omit<MedicalHoMutationArg, 'category'>,
      to: Omit<MedicalHoMutationArg, 'category'>
    ) => {
      this.mergeMhoInPlace(
        {
          mergeFrom: { category, ...from },
          mergeTo: { category, ...to }
        },
        medicalHo,
        (mho, which) => {
          if (which === 'from') {
            mho.translationQcode = undefined; // do not translate qcode
          }
          return mho;
        }
      );
    };

    doMerge(
      MedicalHoItemCategory.CARDIO_VASCULAR,
      { qcode: 'HOVenousThrombosisDetailsLastEpisodeDate' },
      { qcode: 'HOVenousThrombosis', code: 'YES' }
    );
    doMerge(
      MedicalHoItemCategory.NEURO,
      { qcode: 'EpilepsyHOLastEpisodeDate' },
      { qcode: 'EpilepsyHO', code: 'YES' }
    );
    doMerge(
      MedicalHoItemCategory.NEURO,
      { qcode: 'StrokeHODetailsLastEpisodeDate' },
      { qcode: 'StrokeHO', code: 'YES' }
    );
    doMerge(
      MedicalHoItemCategory.CARDIO_VASCULAR,
      { qcode: 'HeartAttackHODate' },
      { qcode: 'CardiologyHO', code: 'HeartAttackHO' }
    );
  }

  private mergeAsthmaInPlace(medicalHo: MedicalHo) {
    const doMerge = (toQcode: string, fromQcode: string) => {
      this.mergeMhoInPlace(
        {
          mergeFrom: {
            category: MedicalHoItemCategory.PULMONARY,
            qcode: fromQcode
          },
          mergeTo: {
            category: MedicalHoItemCategory.PULMONARY,
            qcode: toQcode
          }
        },
        medicalHo,
        this.adjustMergeItemsTranslationsInPlace
      );
    };

    doMerge('Asthma', 'AsthmaDetailsSeverity');
    doMerge('Asthma', 'AsthmaHOIcu');
  }

  private mergeSmokingMhoInPlace(medicalHo: MedicalHo) {
    const doMerge = (toQcode: string, fromQcode: string) => {
      this.mergeMhoInPlace(
        {
          mergeFrom: {
            category: MedicalHoItemCategory.PULMONARY,
            qcode: fromQcode
          },
          mergeTo: {
            category: MedicalHoItemCategory.PULMONARY,
            qcode: toQcode
          }
        },
        medicalHo,
        this.adjustMergeItemsTranslationsInPlace
      );
    };

    const smokerMho = this.findSingleMho(
      { category: MedicalHoItemCategory.PULMONARY, qcode: 'Smoker' },
      medicalHo
    );

    if (smokerMho?.code === 'Smoker') {
      const smokerWantsToStopMho = this.findSingleMho(
        {
          category: MedicalHoItemCategory.PULMONARY,
          qcode: 'SmokerWantToStop'
        },
        medicalHo
      );
      if (smokerWantsToStopMho) {
        smokerWantsToStopMho.removedWhenMergedIds = [smokerMho.id];
        const smokerNotes = smokerMho.notes;
        if (smokerNotes) {
          if (smokerWantsToStopMho.notes) {
            smokerWantsToStopMho.notes += '\n';
          } else {
            smokerWantsToStopMho.notes = '';
          }
          smokerWantsToStopMho.notes += smokerNotes;
        }
      }

      //active smoker
      this.removeMhoInPlace(
        {
          category: MedicalHoItemCategory.PULMONARY,
          qcode: 'Smoker',
          code: 'Smoker'
        },
        medicalHo
      );
      doMerge('SmokerWantToStop', 'SmokerCigPerDay');
      doMerge('SmokerWantToStop', 'SmokerFirstCigDelay');
      doMerge('SmokerWantToStop', 'SmokerIntensity');
    } else if (smokerMho?.code.startsWith('Smoker.YesButStopped')) {
      //not active smoker
      doMerge('Smoker', 'SmokerIntensity');
    }
  }

  private mergeHeartAttackHoInPlace(medicalHo: MedicalHo) {
    this.mergeMhoInPlace(
      {
        mergeTo: {
          qcode: 'CardiologyHO',
          code: 'HeartAttackHO',
          category: MedicalHoItemCategory.CARDIO_VASCULAR
        },
        mergeFrom: {
          qcode: 'HeartAttackHODetails',
          category: MedicalHoItemCategory.CARDIO_VASCULAR
        }
      },
      medicalHo,
      this.adjustMergeItemsTranslationsInPlace
    );
  }

  private mergeHOVenousThrombosisInPlace(medicalHo: MedicalHo) {
    const doMerge = (sourceQcode: string) =>
      this.mergeMhoInPlace(
        {
          mergeTo: {
            qcode: 'HOVenousThrombosis',
            code: 'YES',
            category: MedicalHoItemCategory.CARDIO_VASCULAR
          },
          mergeFrom: {
            qcode: sourceQcode,
            category: MedicalHoItemCategory.CARDIO_VASCULAR
          }
        },
        medicalHo,
        this.adjustMergeItemsTranslationsInPlace
      );

    doMerge('HOVenousThrombosisDetails');
    doMerge('HOVenousThrombosisDetailsPlus');
    doMerge('HOVenousThrombosisDetailsFrequency');
  }

  private mergeStrokeHoInPlace(medicalHo: MedicalHo) {
    const doMerge = (sourceQcode: string) =>
      this.mergeMhoInPlace(
        {
          mergeTo: {
            qcode: 'StrokeHO',
            code: 'YES',
            category: MedicalHoItemCategory.NEURO
          },
          mergeFrom: {
            qcode: sourceQcode,
            category: MedicalHoItemCategory.NEURO
          }
        },
        medicalHo,
        this.adjustMergeItemsTranslationsInPlace
      );

    doMerge('StrokeHODetails');
    doMerge('StrokeHODetailsPlus');
    doMerge('StrokeHODetailsFrequency');
  }

  private mergeRaiScoreInPlace(medicalHo: MedicalHo) {
    const doMerge = (sourceQcode: string) =>
      this.mergeMhoInPlace(
        {
          mergeTo: {
            qcode: 'RAI',
            category: MedicalHoItemCategory.GENERAL
          },
          mergeFrom: {
            qcode: sourceQcode,
            category: MedicalHoItemCategory.GENERAL
          }
        },
        medicalHo,
        this.adjustMergeItemsTranslationsInPlace
      );

    doMerge('OneYearMortalityRate');
    doMerge('FrailtyStatus');
  }

  // LegalGuardian is a multi-select, so several items share the same qcode
  // (one per chosen guardian). Merge them all under the first so a single
  // "Filled by" badge is displayed instead of one per selection.
  private mergeLegalGuardianInPlace(medicalHo: MedicalHo) {
    const arg: MedicalHoMutationArg = {
      category: MedicalHoItemCategory.GENERAL,
      qcode: 'LegalGuardian'
    };
    const items = this.findMho(arg, medicalHo);
    if (!items || items.length < 2) {
      return;
    }

    const [mergeTo, ...mergeFrom] = items;
    this.appendMergedItemsInPlace(mergeTo, mergeFrom, (mho, which) => {
      if (which === 'from') {
        mho.translationQcode = undefined; // do not repeat the question prefix
      }
      return mho;
    });

    const mergeFromSet = new Set(mergeFrom);
    const initialLength = medicalHo[arg.category]?.length;
    medicalHo[arg.category] = medicalHo[arg.category]?.filter(
      (it) => !mergeFromSet.has(it)
    );
    if (initialLength !== medicalHo[arg.category]?.length) {
      this.removedItemsWhileMerging.push(arg);
    }
  }

  // Fold the prematurity details into the "PrematureBirth.YES" badge.
  // BirthWeightKg is triaged into ANESTHESIA (not GENERAL like the parent),
  // so it is merged across categories.
  private mergePrematureBirthInPlace(medicalHo: MedicalHo) {
    const doMerge = (mergeFrom: MedicalHoMutationArg) =>
      this.mergeMhoInPlace(
        {
          mergeTo: {
            qcode: 'PrematureBirth',
            code: 'YES',
            category: MedicalHoItemCategory.GENERAL
          },
          mergeFrom
        },
        medicalHo,
        this.adjustMergeItemsTranslationsInPlace
      );

    doMerge({
      qcode: 'PrematureBirthTerm',
      category: MedicalHoItemCategory.GENERAL
    });
    doMerge({
      qcode: 'BirthWeightKg',
      category: MedicalHoItemCategory.ANESTHESIA
    });
  }

  private adjustMergeItemsTranslationsInPlace(
    mho: MedicalHoItem
  ): MedicalHoItem {
    if (
      needsQuestionTranslation(mho) &&
      !mho.translationQcode?.endsWith('.short')
    ) {
      mho.translationQcode = `${mho.translationQcode}.short`;
    }
    if (isTranslatable(mho) && !mho.translationCode?.endsWith('.short')) {
      mho.translationCode = `${mho.translationCode}.short`;
    }
    return mho;
  }

  private correctUserInputHeartAttackHODetailsTranslationQCode(
    mho: MedicalHoItem
  ) {
    if (
      mho.sourceType === SourceType.PATIENT_INPUT &&
      mho.qcode === 'HeartAttackHODetails'
    ) {
      mho.translationQcode =
        'medical_synthesis.TRIAGE_HeartAttackHODetails.Other';
    }

    return mho;
  }
}
